/**
 * Meal Detail Modal
 * Shared by the Meal Plan Builder and Featured Collection sections
 *
 * Usage:
 *   MrkMealModal.open(product)                        -> "Add to Plan" button (handled by the builder)
 *   MrkMealModal.open(product, { linkUrl, linkLabel }) -> button becomes a link
 */

(function () {
  'use strict';

  if (window.MrkMealModal) return;

  const MrkMealModal = {
    modal: null,
    currentProductId: null,

    /**
     * Get the modal element. Sections that can appear more than once on a page
     * ship the modal inside a <template>, so it is only added to the page once.
     */
    getModal: function () {
      if (this.modal && document.body.contains(this.modal)) return this.modal;

      let modal = document.getElementById('mrk-meal-modal');
      if (!modal) {
        const template = document.querySelector('template[data-mrk-meal-modal-template]');
        if (!template) return null;
        document.body.appendChild(template.content.cloneNode(true));
        modal = document.getElementById('mrk-meal-modal');
      }

      this.modal = modal;
      this.bindEvents(modal);
      return modal;
    },

    bindEvents: function (modal) {
      const self = this;
      if (modal.dataset.mrkModalBound) return;
      modal.dataset.mrkModalBound = 'true';

      modal.addEventListener('click', function (e) {
        if (e.target.closest('[data-modal-close]')) {
          self.close();
          return;
        }

        // Nutrition tab switching
        const tab = e.target.closest('.mrk-nutrition-tab');
        if (tab) {
          modal.querySelectorAll('.mrk-nutrition-tab').forEach(function (el) {
            el.classList.toggle('active', el === tab);
          });
          modal.querySelectorAll('.mrk-nutrition-content').forEach(function (el) {
            el.classList.toggle('active', el.id === tab.dataset.tab);
          });
        }
      });

      // Close modal on escape key
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.isOpen()) {
          self.close();
        }
      });
    },

    isOpen: function () {
      return !!this.modal && this.modal.classList.contains('mrk-modal-open');
    },

    /**
     * Open the modal for a product (see snippets/mrk-product-json.liquid)
     */
    open: function (product, options) {
      const modal = this.getModal();
      if (!modal || !product) return;
      options = options || {};

      this.populate(product);
      this.currentProductId = product.id;

      // Action button: add to plan, or a link (e.g. to the Build Your Plan page)
      const addButton = modal.querySelector('.mrk-btn-modal-add');
      const linkButton = modal.querySelector('[data-modal-link]');
      const useLink = !!options.linkUrl;

      if (addButton) addButton.style.display = useLink ? 'none' : '';
      if (linkButton) {
        linkButton.style.display = useLink ? '' : 'none';
        if (useLink) {
          linkButton.setAttribute('href', options.linkUrl);
          const label = linkButton.querySelector('[data-modal-link-label]');
          if (label && options.linkLabel) label.textContent = options.linkLabel;
        }
      }

      clearTimeout(this.closeTimer);
      modal.style.display = 'flex';
      const container = modal.querySelector('.mrk-modal-container');
      if (container) container.scrollTop = 0;
      // Force a reflow so the fade-in transition runs
      void modal.offsetWidth;
      modal.classList.add('mrk-modal-open');
      document.body.style.overflow = 'hidden';
    },

    close: function () {
      const modal = this.modal;
      if (!modal) return;

      modal.classList.remove('mrk-modal-open');
      document.body.style.overflow = '';
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(function () {
        modal.style.display = 'none';
      }, 300);
    },

    /**
     * Check if product is new (created within last 60 days)
     */
    isNewProduct: function (createdAt) {
      if (!createdAt) return false;

      const created = new Date(createdAt);
      const now = new Date();
      const daysDiff = Math.floor((now - created) / (1000 * 60 * 60 * 24));

      return daysDiff <= 60;
    },

    /**
     * Populate modal with meal data
     */
    populate: function (product) {
      const modal = this.getModal();
      if (!modal) return;

      const metafields = (product.metafields && product.metafields.custom) || {};

      const setText = function (selector, value) {
        modal.querySelectorAll(selector).forEach(function (el) {
          el.textContent = value;
        });
      };

      // Update image
      const image = modal.querySelector('[data-modal-image]');
      let imageSrc = '';
      if (product.featured_image && product.featured_image.src) {
        imageSrc = product.featured_image.src;
      } else if (product.images && product.images.length > 0 && product.images[0].src) {
        imageSrc = product.images[0].src;
      }
      if (image) {
        image.setAttribute('src', imageSrc);
        image.setAttribute('alt', product.title);
      }

      // Update title
      setText('[data-modal-title]', product.title);

      // Update description
      const description = modal.querySelector('[data-modal-description]');
      if (description) description.innerHTML = product.description || '';

      // Update nutritional highlights
      const calories = metafields.calories_per_serving || '-';
      const protein = metafields.protein_per_serving || '-';
      const carbs = metafields.carbs_per_serving || '-';
      const fats = metafields.fat_per_serving || '-';
      const portionSize = metafields.portion_size || '-';

      setText('[data-value="portion"]', portionSize);
      setText('[data-value="calories"]', String(calories).split(' ')[0]);
      setText('[data-value="protein"]', protein);
      setText('[data-value="carbs"]', carbs);
      setText('[data-value="fats"]', fats);

      // Update ingredients
      const ingredients = metafields.ingredients_list || [];
      const ingredientsEl = modal.querySelector('[data-modal-ingredients]');
      if (ingredientsEl) {
        if (Array.isArray(ingredients) && ingredients.length > 0) {
          ingredientsEl.innerHTML = ingredients.join(', ');
        } else {
          ingredientsEl.textContent = 'Not available';
        }
      }

      // Update allergens
      const allergens = metafields.allergen_information || [];
      if (Array.isArray(allergens) && allergens.length > 0) {
        setText('[data-modal-allergens]', allergens.join(', '));
      } else {
        setText('[data-modal-allergens]', 'None');
      }

      // Update nutrition table (per serving)
      setText('[data-serving="calories"]', calories);
      setText('[data-serving="energy-kj"]', metafields.energy_kj_per_serving || '-');
      setText('[data-serving="fat"]', metafields.fat_per_serving || '-');
      setText('[data-serving="saturates"]', metafields.saturates_per_serving || '-');
      setText('[data-serving="carbs"]', carbs);
      setText('[data-serving="sugars"]', metafields.sugars_per_serving || '-');
      setText('[data-serving="fibre"]', metafields.fibre_per_serving || '-');
      setText('[data-serving="protein"]', protein);
      setText('[data-serving="salt"]', metafields.salt_per_serving || '-');

      // Update nutrition table (per 100g)
      setText('[data-per100="calories"]', metafields.calories_per_100g || '-');
      setText('[data-per100="energy-kj"]', metafields.energy_kj_per_100g || '-');
      setText('[data-per100="fat"]', metafields.fat_per_100g || '-');
      setText('[data-per100="saturates"]', metafields.saturates_per_100g || '-');
      setText('[data-per100="carbs"]', metafields.carbs_per_100g || '-');
      setText('[data-per100="sugars"]', metafields.sugars_per_100g || '-');
      setText('[data-per100="fibre"]', metafields.fibre_per_100g || '-');
      setText('[data-per100="protein"]', metafields.protein_per_100g || '-');
      setText('[data-per100="salt"]', metafields.salt_per_100g || '-');

      // Reset to the "Per Portion" tab
      modal.querySelectorAll('.mrk-nutrition-tab').forEach(function (el, index) {
        el.classList.toggle('active', index === 0);
      });
      modal.querySelectorAll('.mrk-nutrition-content').forEach(function (el, index) {
        el.classList.toggle('active', index === 0);
      });

      // Update badges
      const badges = modal.querySelector('[data-modal-badges]');
      if (badges) {
        badges.innerHTML = '';

        const addBadge = function (className, text) {
          const badge = document.createElement('span');
          badge.className = 'mrk-badge ' + className;
          badge.textContent = text;
          badges.appendChild(badge);
        };

        // Check if product is new (within 60 days)
        if (product.created_at && this.isNewProduct(product.created_at)) {
          addBadge('mrk-badge-new', 'New');
        }

        // Under 600 calories badge
        if (calories && parseInt(calories) < 600) {
          addBadge('mrk-badge-under-600', 'Under 600kcals');
        }

        const isVegetarian = metafields.vegetarian === true || metafields.vegetarian === 'true';
        if (isVegetarian) {
          addBadge('mrk-badge-vegetarian', 'Vegetarian');
        }

        const carbLevel = metafields.carb_level;
        if (carbLevel) {
          const carbClass = String(carbLevel).toLowerCase().replace(/\s/g, '-');
          addBadge('mrk-badge-carb mrk-badge-' + carbClass, carbLevel);
        }
      }
    }
  };

  window.MrkMealModal = MrkMealModal;

  /* ------------------------------------------------------------------
   * Meal cards anywhere in the theme
   *
   * Any product card marked as a meal opens this popup instead of doing
   * nothing, so the collection page, featured collections, recently viewed
   * products and any section added later all behave the same way.
   * ------------------------------------------------------------------ */

  const config = window.MrkMealPopupConfig || {};
  const productCache = {};

  /**
   * Meal details, taken from the card when they are already on the page
   * (meal plan builder) or loaded from the meal JSON template
   */
  function loadProduct(card) {
    const inline = card.querySelector('[data-product-json]');
    if (inline) {
      try {
        return Promise.resolve(JSON.parse(inline.textContent));
      } catch (error) {
        // Fall through to the request below
      }
    }

    const handle = card.dataset.productHandle;
    if (!handle) return Promise.reject(new Error('Meal popup: the card has no product handle'));
    if (productCache[handle]) return productCache[handle];

    const root = (config.rootUrl || '/').replace(/\/$/, '');
    const request = fetch(root + '/products/' + handle + '?view=meal-json')
      .then(function (response) {
        if (!response.ok) throw new Error('Meal popup: unable to load ' + handle);
        return response.text();
      })
      .then(function (text) {
        const holder = document.createElement('div');
        holder.innerHTML = text;
        const json = holder.querySelector('[data-product-json]');
        if (!json) {
          throw new Error('Meal popup: no meal details for ' + handle + '. Is templates/product.meal-json.liquid uploaded?');
        }
        return JSON.parse(json.textContent);
      })
      .catch(function (error) {
        delete productCache[handle]; // so the next click tries again
        throw error;
      });

    productCache[handle] = request;
    return request;
  }

  document.addEventListener('click', function (e) {
    if (config.enabled === false) return;

    const trigger = e.target.closest('.m-product-card__link, .m-product-card__name, [data-meal-popup-trigger]');
    if (!trigger) return;

    // Only cards marked as meals, and not inside a section where the popup is turned off
    const card = trigger.closest('.m-product-card[data-meal-popup]');
    if (!card || card.closest('[data-meal-popup-off]')) return;

    e.preventDefault();
    if (card.classList.contains('mrk-meal-card-loading')) return;
    card.classList.add('mrk-meal-card-loading');

    loadProduct(card)
      .then(function (product) {
        card.classList.remove('mrk-meal-card-loading');
        MrkMealModal.open(product, {
          linkUrl: config.buttonUrl || '/pages/build-your-plan',
          linkLabel: config.buttonLabel,
        });
      })
      .catch(function (error) {
        card.classList.remove('mrk-meal-card-loading');
        console.error(error);

        // Rather than leave the click doing nothing, open the product page
        const url = trigger.getAttribute('href');
        if (url && url.indexOf('javascript:') !== 0) window.location.href = url;
      });
  });
})();
